
import type { Vote, VoteInput, SubmitResult } from './types'

/**
 * Local persistence layer that emulates the Supabase `votes` table,
 * duplicate prevention, and Realtime subscriptions.
 *
 * To move to Supabase later, swap the bodies of these functions for
 * supabase-js calls (insert/select/delete/update) and replace the
 * pub/sub below with `supabase.channel('votes').on('postgres_changes', ...)`.
 */

const VOTES_KEY = 'retreat.votes.v1'
const AUTH_KEY = 'retreat.admin.v1'
const GOOGLE_SHEETS_ENDPOINT =
  'https://script.google.com/macros/s/AKfycbyAyEUFSTgj2igyI0k-8BFAq88lf9w9vxs9XQm3rcJmnwIlOwhKk9fhWFIH2TREZxhn/exec'

type Listener = () => void
const listeners = new Set<Listener>()

function read(): Vote[] {
  try {
    const raw = localStorage.getItem(VOTES_KEY)
    return raw ? (JSON.parse(raw) as Vote[]) : []
  } catch {
    return []
  }
}

function persist(votes: Vote[]) {
  localStorage.setItem(VOTES_KEY, JSON.stringify(votes))
  emit()
}

function emit() {
  listeners.forEach((l) => l())
}

// Cross-tab realtime: react to storage changes from other tabs.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === VOTES_KEY) emit()
  })
}

/** Subscribe to any change in the votes table (Realtime emulation). */
export function subscribe(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getVotes(): Vote[] {
  return read().sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  )
}

function normalizeName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ')
}

/** Duplicate if the email OR the full name already exists. */
export function isDuplicate(fullName: string, email: string): boolean {
  const votes = read()
  const e = email.trim().toLowerCase()
  const n = normalizeName(fullName)
  return votes.some(
    (v) => v.email === e || normalizeName(v.fullName) === n,
  )
}

/**
 * Sends a vote to the deployed Google Apps Script Web App.
 * Form encoding is a CORS-safe browser request and maps directly to the
 * script's e.parameter fields (fullName, email, city, province, timezone).
 */
export async function submitVoteToGoogleSheet(input: VoteInput): Promise<void> {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  const formData = new URLSearchParams({
    fullName: input.fullName.trim(),
    email: input.email.trim().toLowerCase(),
    city: input.city,
    province: input.province,
    timezone,
  })

  await fetch(GOOGLE_SHEETS_ENDPOINT, {
    method: 'POST',
    mode: 'no-cors',
    body: formData,
  })
}

export function submitVote(input: VoteInput): SubmitResult {
  const fullName = input.fullName.trim()
  const email = input.email.trim().toLowerCase()

  if (isDuplicate(fullName, email)) {
    return { ok: false, reason: 'duplicate' }
  }

  const now = new Date()
  const timezone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || undefined

  const vote: Vote = {
    id:
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    fullName,
    email,
    city: input.city,
    province: input.province,
    date: now.toISOString().slice(0, 10),
    time: now.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
    timestamp: now.toISOString(),
    timezone,
  }

  const votes = read()
  votes.push(vote)
  persist(votes)
  return { ok: true, vote }
}

export function deleteVote(id: string) {
  persist(read().filter((v) => v.id !== id))
}

export function updateVoteEmail(id: string, email: string) {
  const votes = read().map((v) =>
    v.id === id ? { ...v, email: email.trim().toLowerCase() } : v,
  )
  persist(votes)
}

/* ---- Admin auth (Supabase Auth emulation) ---- */

// Demo credentials — replace with supabase.auth.signInWithPassword.
const ADMIN_EMAIL = 'admin@retreat.vote'
const ADMIN_PASSWORD = 'retreat2026'

export function signIn(email: string, password: string): boolean {
  const ok =
    email.trim().toLowerCase() === ADMIN_EMAIL && password === ADMIN_PASSWORD
  if (ok) localStorage.setItem(AUTH_KEY, '1')
  return ok
}

export function signOut() {
  localStorage.removeItem(AUTH_KEY)
}

export function isAuthed(): boolean {
  return localStorage.getItem(AUTH_KEY) === '1'
}

export const ADMIN_HINT = { email: ADMIN_EMAIL, password: ADMIN_PASSWORD }

/* ---- Seed a little demo data on first load so charts aren't empty ---- */
export function seedIfEmpty() {
  if (read().length > 0) return
  const seed: Array<[string, string, string, string]> = [
    ['Ava Thompson', 'ava@example.com', 'Vancouver', 'British Columbia'],
    ['Liam Chen', 'liam@example.com', 'Calgary', 'Alberta'],
    ['Noah Patel', 'noah@example.com', 'Vancouver', 'British Columbia'],
    ['Emma Roy', 'emma@example.com', 'Montreal', 'Quebec'],
    ['Olivia Singh', 'olivia@example.com', 'Toronto', 'Ontario'],
    ['Sophia Nguyen', 'sophia@example.com', 'Halifax', 'Nova Scotia'],
    ['Mason Brown', 'mason@example.com', 'Vancouver', 'British Columbia'],
    ['Isabella Cote', 'isabella@example.com', 'Toronto', 'Ontario'],
    ['James Wilson', 'james@example.com', 'Calgary', 'Alberta'],
    ['Charlotte Lee', 'charlotte@example.com', 'Montreal', 'Quebec'],
  ]
  const base = Date.now() - 1000 * 60 * 60 * 26
  const votes: Vote[] = seed.map(([fullName, email, city, province], i) => {
    const d = new Date(base + i * 1000 * 60 * 47)
    return {
      id: `seed-${i}`,
      fullName,
      email,
      city,
      province,
      date: d.toISOString().slice(0, 10),
      time: d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
      timestamp: d.toISOString(),
      timezone: 'America/Toronto',
    }
  })
  persist(votes)
}

